import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useParams } from 'react-router'
import {
  FLAG_TYPES,
  getActiveClaimFlag,
  useAddFlagComment,
  useCreateDocumentFlag,
  useDocumentFlags,
  useResolveDocumentFlag,
  useUpdateDocumentFlag,
} from '~/entities/document-flags'
import { useEobOnboardingSelectors } from '~/features/eob-onboarding'
import { apiClient } from '~/shared/api/data-ingestion-api'
import { cn } from '~/shared/lib/cn'
import { useIsScrolled } from '~/shared/lib/use-is-scrolled'
import { Button, Chip, IconButton, ScrollArea, Text, Tooltip, toast } from '~/shared/ui'
import { useCreatePatientClaim, useDeletePatientClaim, useUpdatePatientClaim } from '../../api'
import { ClaimsSidebar } from './claims-sidebar'
import { DeleteClaimDialog } from './delete-claim-dialog'
import { FlagClaimPopover } from './flag-claim-popover'
import type { PatientClaimFormRef } from './patient-claim-form'
import { PatientClaimForm } from './patient-claim-form'

function getDisplayName(firstName: string, lastName: string): string | null {
  const parts = [firstName, lastName].filter(Boolean)
  return parts.length > 0 ? parts.join(' ') : null
}

interface PatientClaimDataProps {
  claimsData: apiClient.PatientClaimsResponse
  checkedPatients?: apiClient.CheckedPatient[]
  onScrolledBottomChange?: (isScrolledBottom: boolean) => void
  onAllReviewedChange?: (allReviewed: boolean) => void
  onModifiedChange?: (isModified: boolean) => void
}

export function PatientClaimData({
  claimsData,
  checkedPatients,
  onScrolledBottomChange,
  onAllReviewedChange,
  onModifiedChange,
}: PatientClaimDataProps) {
  const documentKey = useParams<'documentKey'>().documentKey as string
  const createClaim = useCreatePatientClaim()
  const updateClaim = useUpdatePatientClaim()
  const deleteClaim = useDeletePatientClaim()
  const createFlag = useCreateDocumentFlag()
  const updateFlag = useUpdateDocumentFlag()
  const resolveFlag = useResolveDocumentFlag()
  const addFlagComment = useAddFlagComment()
  const { data: flagsData } = useDocumentFlags()
  const documentFlags = useMemo(() => flagsData?.items ?? [], [flagsData?.items])
  const { hasSeenPatientClaimOnboarding, setIsPatientClaimOnboardingOpen } =
    useEobOnboardingSelectors()
  const [deleteDialogKey, setDeleteDialogKey] = useState<string | null>(null)

  const serverClaims = claimsData.patient_claims
  const [initialDrafts] = useState<apiClient.PatientClaimResponse[]>(() => {
    if (serverClaims.length > 0) return []
    if (checkedPatients?.length) {
      return checkedPatients.map(
        (p) =>
          ({
            key: `draft:${crypto.randomUUID()}`,
            patient_account_number: p.patient_account_number ?? undefined,
            patient_first_name: p.patient_first_name ?? undefined,
            patient_last_name: p.patient_last_name ?? undefined,
          }) as apiClient.PatientClaimResponse,
      )
    }
    return [{ key: `draft:${crypto.randomUUID()}` } as apiClient.PatientClaimResponse]
  })
  const [draftClaims, setDraftClaims] = useState<apiClient.PatientClaimResponse[]>(
    () => initialDrafts,
  )
  const [selectedClaimKey, setSelectedClaimKey] = useState<string | null>(
    () => serverClaims[0]?.key ?? initialDrafts[0]?.key ?? null,
  )

  const formRefsMap = useRef(new Map<string, PatientClaimFormRef>())

  /** Combined list: server claims first, then local drafts */
  const allClaims = useMemo(() => [...serverClaims, ...draftClaims], [serverClaims, draftClaims])

  const selectedClaim = allClaims.find((c) => c.key === selectedClaimKey) ?? null
  const selectedIndex = selectedClaim ? allClaims.findIndex((c) => c.key === selectedClaimKey) : -1

  /** Live name overrides from form state, keyed by claim key. */
  const nameOverridesRef = useRef(new Map<string, { firstName: string; lastName: string }>())
  const [, forceUpdate] = useState(0)

  /** Track modified state per claim form. */
  const modifiedMapRef = useRef(new Map<string, boolean>())
  const handleModifiedChange = useCallback((key: string, isModified: boolean) => {
    const prev = modifiedMapRef.current.get(key)
    if (prev === isModified) return
    modifiedMapRef.current.set(key, isModified)
    forceUpdate((n) => n + 1)
  }, [])

  /** Track validity state per claim form. */
  const validMapRef = useRef(new Map<string, boolean>())
  const handleValidChange = useCallback((key: string, isValid: boolean) => {
    const prev = validMapRef.current.get(key)
    if (prev === isValid) return
    validMapRef.current.set(key, isValid)
    forceUpdate((n) => n + 1)
  }, [])

  /** Track undo/redo availability per claim form. */
  const canUndoMapRef = useRef(new Map<string, boolean>())
  const handleCanUndoChange = useCallback((key: string, canUndo: boolean) => {
    const prev = canUndoMapRef.current.get(key)
    if (prev === canUndo) return
    canUndoMapRef.current.set(key, canUndo)
    forceUpdate((n) => n + 1)
  }, [])

  const canRedoMapRef = useRef(new Map<string, boolean>())
  const handleCanRedoChange = useCallback((key: string, canRedo: boolean) => {
    const prev = canRedoMapRef.current.get(key)
    if (prev === canRedo) return
    canRedoMapRef.current.set(key, canRedo)
    forceUpdate((n) => n + 1)
  }, [])

  const localFlagMapRef = useRef(new Map<string, { notes: string } | null>())
  const localFlagLockedKeysRef = useRef(new Set<string>())
  const handleLocalFlagChange = useCallback((key: string, localFlag: { notes: string } | null) => {
    if (localFlagLockedKeysRef.current.has(key)) return
    const prev = localFlagMapRef.current.get(key)
    if (prev === localFlag) return
    localFlagMapRef.current.set(key, localFlag)
    forceUpdate((n) => n + 1)
  }, [])

  const handleNameChange = useCallback((key: string, firstName: string, lastName: string) => {
    const prev = nameOverridesRef.current.get(key)
    if (prev?.firstName === firstName && prev?.lastName === lastName) return
    nameOverridesRef.current.set(key, { firstName, lastName })
    forceUpdate((n) => n + 1)
  }, [])

  const handleAddDraftClaim = useCallback(() => {
    const newKey = `draft:${crypto.randomUUID()}`
    const draft: apiClient.PatientClaimResponse = { key: newKey }
    setDraftClaims((prev) => [...prev, draft])
    setSelectedClaimKey(newKey)
  }, [])

  const handleResolveFlag = useCallback(
    async (flagKey: string, resolutionNotes: string) => {
      try {
        if (resolutionNotes.trim()) {
          await addFlagComment.mutateAsync({
            path: { key: documentKey, flagKey },
            body: { notes: resolutionNotes },
          })
        }
        await resolveFlag.mutateAsync({
          path: { key: documentKey, flagKey },
          body: {},
        })
      } catch {
        toast('Failed to resolve flag')
      }
    },
    [documentKey, resolveFlag, addFlagComment],
  )

  const isSaving = createClaim.isPending || updateClaim.isPending || createFlag.isPending

  const handleSave = useCallback(async () => {
    if (!selectedClaimKey) return

    const formRef = formRefsMap.current.get(selectedClaimKey)
    if (!formRef) return

    const body = formRef.getRequestBody()
    const isDraft = selectedClaimKey.startsWith('draft:')
    const localFlag = localFlagMapRef.current.get(selectedClaimKey) ?? null

    const isModified = modifiedMapRef.current.get(selectedClaimKey) ?? false
    let claimKey: string
    if (isDraft) {
      const response = await createClaim.mutateAsync({
        path: { document_key: documentKey },
        body,
      })
      setDraftClaims((prev) => prev.filter((c) => c.key !== selectedClaimKey))
      setSelectedClaimKey(response.key)
      claimKey = response.key
      toast('Patient claim saved')
      if (!hasSeenPatientClaimOnboarding) {
        setIsPatientClaimOnboardingOpen(true)
      }
    } else if (isModified) {
      const response = await updateClaim.mutateAsync({
        path: { document_key: documentKey, claim_key: selectedClaimKey },
        body,
      })
      formRef.resetToServerResponse(response)
      claimKey = selectedClaimKey
      toast('Patient claim saved')
    } else {
      claimKey = selectedClaimKey
    }

    if (localFlag) {
      localFlagLockedKeysRef.current.add(claimKey)
      try {
        await createFlag.mutateAsync({
          path: { key: documentKey },
          body: {
            flag_type: FLAG_TYPES.PATIENT_CLAIM,
            document_step: 'eob_processing',
            notes: localFlag.notes.trim() ? localFlag.notes : null,
            metadata: {
              patient_claim_key: claimKey,
              reviewed: validMapRef.current.get(claimKey) ?? false,
            },
          },
        })
      } catch {
        toast('Failed to create flag')
      }
      localFlagLockedKeysRef.current.delete(claimKey)
      localFlagMapRef.current.delete(claimKey)
      forceUpdate((n) => n + 1)
    }

    if (!localFlag) {
      const persistedFlag = getActiveClaimFlag(documentFlags, claimKey)
      const isValid = validMapRef.current.get(claimKey) ?? false
      const wasReviewed = persistedFlag?.metadata?.reviewed === true
      if (persistedFlag && wasReviewed !== isValid) {
        try {
          await updateFlag.mutateAsync({
            path: { key: documentKey, flagKey: persistedFlag.key },
            body: { metadata: { ...persistedFlag.metadata, reviewed: isValid } },
          })
        } catch {
          toast('Failed to update flag')
        }
      }
    }
  }, [
    selectedClaimKey,
    documentKey,
    createClaim,
    updateClaim,
    createFlag,
    updateFlag,
    documentFlags,
    hasSeenPatientClaimOnboarding,
    setIsPatientClaimOnboardingOpen,
  ])

  const handleDeleteClick = useCallback(() => {
    if (!selectedClaimKey) return
    setDeleteDialogKey(selectedClaimKey)
  }, [selectedClaimKey])

  const handleDeleteConfirm = useCallback(async () => {
    if (!deleteDialogKey) return

    const isDraft = deleteDialogKey.startsWith('draft:')

    if (isDraft) {
      setDraftClaims((prev) => prev.filter((c) => c.key !== deleteDialogKey))
    } else {
      await deleteClaim.mutateAsync({
        path: { document_key: documentKey, claim_key: deleteDialogKey },
      })
      toast('Patient claim deleted')
    }

    setDeleteDialogKey(null)
    nameOverridesRef.current.delete(deleteDialogKey)
    const remaining = allClaims.filter((c) => c.key !== deleteDialogKey)
    const deletedIndex = allClaims.findIndex((c) => c.key === deleteDialogKey)

    if (remaining.length === 0) {
      const newKey = `draft:${crypto.randomUUID()}`
      const draft: apiClient.PatientClaimResponse = { key: newKey }
      setDraftClaims((prev) => [...prev, draft])
      setSelectedClaimKey(newKey)
    } else {
      setSelectedClaimKey(remaining[Math.min(deletedIndex, remaining.length - 1)]?.key ?? null)
    }
  }, [deleteDialogKey, documentKey, deleteClaim, allClaims])

  const { isScrolled: isScrolledTop, sentinelElement: sentinelElementTop } = useIsScrolled()
  const { isScrolled: isScrolledBottom, sentinelElement: sentinelElementBottom } = useIsScrolled()

  useEffect(() => {
    onScrolledBottomChange?.(isScrolledBottom)
  }, [isScrolledBottom, onScrolledBottomChange])

  const allClaimsReviewed =
    allClaims.length > 0 &&
    draftClaims.length === 0 &&
    allClaims.every((c) => !(modifiedMapRef.current.get(c.key) ?? false))
  useEffect(() => {
    onAllReviewedChange?.(allClaimsReviewed)
  }, [allClaimsReviewed, onAllReviewedChange])

  const isAnyClaimModified = allClaims.some((c) => modifiedMapRef.current.get(c.key) ?? false)
  useEffect(() => {
    onModifiedChange?.(isAnyClaimModified)
  }, [isAnyClaimModified, onModifiedChange])

  function getClaimDisplayName(claim: apiClient.PatientClaimResponse): string | null {
    const override = nameOverridesRef.current.get(claim.key)
    if (override) return getDisplayName(override.firstName, override.lastName)
    return getDisplayName(claim.patient_first_name ?? '', claim.patient_last_name ?? '')
  }

  return (
    <div className="flex min-h-0 flex-1">
      <ClaimsSidebar
        claims={allClaims}
        selectedClaimKey={selectedClaimKey}
        onSelectClaim={setSelectedClaimKey}
        onAddClaim={handleAddDraftClaim}
        getDisplayName={getClaimDisplayName}
        isReviewed={(claim) =>
          !claim.key.startsWith('draft:') &&
          !(modifiedMapRef.current.get(claim.key) ?? false) &&
          (validMapRef.current.get(claim.key) ?? false)
        }
        getFlagSeverity={(claim) => {
          const localFlag = localFlagMapRef.current.get(claim.key)
          if (localFlag) {
            const isValid = validMapRef.current.get(claim.key) ?? false
            return isValid ? 'low' : 'high'
          }
          const persistedFlag = getActiveClaimFlag(documentFlags, claim.key)
          if (!persistedFlag) return null
          const isValid = validMapRef.current.get(claim.key) ?? false
          return isValid ? 'low' : 'high'
        }}
      />

      <div className="flex min-h-0 flex-1 flex-col">
        <div
          className={cn('flex min-h-[60px] items-center justify-between px-6 py-3', {
            'shadow-popover transition-shadow': isScrolledTop,
          })}
        >
          <div className="flex items-center gap-5">
            <div className="flex flex-col">
              <Text appearance="SUBTITLE2">
                {selectedClaim && getClaimDisplayName(selectedClaim)
                  ? getClaimDisplayName(selectedClaim)
                  : 'New Patient'}
              </Text>
              {allClaims.length > 0 && (
                <Text
                  className="text-text-secondary"
                  appearance="CAPTION"
                >
                  Patient {selectedIndex + 1} of {allClaims.length}
                </Text>
              )}
            </div>
            <div className="flex items-center gap-2">
              {(() => {
                const isDraft = selectedClaimKey?.startsWith('draft:')
                const isModified = selectedClaimKey
                  ? (modifiedMapRef.current.get(selectedClaimKey) ?? false)
                  : false
                const isValid = selectedClaimKey
                  ? (validMapRef.current.get(selectedClaimKey) ?? false)
                  : false
                const hasPersistedFlag = selectedClaimKey
                  ? getActiveClaimFlag(documentFlags, selectedClaimKey) !== null
                  : false
                const hasLocalFlag = selectedClaimKey
                  ? localFlagMapRef.current.get(selectedClaimKey) != null
                  : false
                const hasFlag = hasPersistedFlag || hasLocalFlag
                const isFollowUp = !isDraft && !isModified && hasFlag
                const isPending = !isFollowUp && (isDraft || isModified || !isValid)

                if (isPending) {
                  return (
                    <Chip
                      rounded
                      warning
                    >
                      Pending review
                    </Chip>
                  )
                }
                if (isFollowUp) {
                  return (
                    <Chip
                      rounded
                      attention
                    >
                      Follow Up
                    </Chip>
                  )
                }
                return (
                  <Chip
                    rounded
                    success
                  >
                    Reviewed
                  </Chip>
                )
              })()}
              <FlagClaimPopover
                documentKey={documentKey}
                persistedFlag={
                  selectedClaimKey ? getActiveClaimFlag(documentFlags, selectedClaimKey) : null
                }
                localFlag={
                  selectedClaimKey ? (localFlagMapRef.current.get(selectedClaimKey) ?? null) : null
                }
                onCreateFlag={(notes) => {
                  if (!selectedClaimKey) return
                  formRefsMap.current.get(selectedClaimKey)?.setLocalFlag({ notes })
                }}
                onUpdateFlag={(notes) => {
                  if (!selectedClaimKey) return
                  formRefsMap.current.get(selectedClaimKey)?.setLocalFlag({ notes })
                }}
                onRemoveFlag={() => {
                  if (!selectedClaimKey) return
                  formRefsMap.current.get(selectedClaimKey)?.setLocalFlag(null)
                }}
                onResolveFlag={handleResolveFlag}
              />
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Tooltip content="Undo">
              <IconButton
                size="small"
                container="small"
                materialIcon="undo"
                title="Undo"
                disabled={!selectedClaimKey || !canUndoMapRef.current.get(selectedClaimKey)}
                onClick={() =>
                  selectedClaimKey && formRefsMap.current.get(selectedClaimKey)?.undo()
                }
              />
            </Tooltip>
            <Tooltip content="Redo">
              <IconButton
                size="small"
                container="small"
                materialIcon="redo"
                title="Redo"
                disabled={!selectedClaimKey || !canRedoMapRef.current.get(selectedClaimKey)}
                onClick={() =>
                  selectedClaimKey && formRefsMap.current.get(selectedClaimKey)?.redo()
                }
              />
            </Tooltip>
            {!(allClaims.length === 1 && allClaims[0].key.startsWith('draft:')) && (
              <Tooltip content="Delete claim">
                <IconButton
                  size="small"
                  container="small"
                  materialIcon="delete"
                  title="Delete claim"
                  disabled={!selectedClaimKey}
                  onClick={handleDeleteClick}
                />
              </Tooltip>
            )}
            {(() => {
              const isDraft = selectedClaimKey?.startsWith('draft:')
              const isModified = selectedClaimKey
                ? (modifiedMapRef.current.get(selectedClaimKey) ?? false)
                : false
              const isValid = selectedClaimKey
                ? (validMapRef.current.get(selectedClaimKey) ?? false)
                : false
              const hasLocalFlag =
                selectedClaimKey &&
                localFlagMapRef.current.get(selectedClaimKey) !== null &&
                localFlagMapRef.current.get(selectedClaimKey) !== undefined
              const hasPersistedFlag = selectedClaimKey
                ? getActiveClaimFlag(documentFlags, selectedClaimKey) !== null
                : false
              const hasFlag = hasLocalFlag || hasPersistedFlag
              const isDefaultValue = !isDraft && !isModified
              const showSave = hasFlag && !isDefaultValue && !isValid

              return (
                <Button
                  raised
                  size="small"
                  onClick={handleSave}
                  disabled={isDefaultValue || isSaving || (!hasFlag && !isValid)}
                >
                  {showSave ? 'Save' : 'Mark as Reviewed'}
                </Button>
              )
            })()}
          </div>
        </div>

        <ScrollArea className="min-h-0 flex-1">
          {sentinelElementTop}

          {allClaims.map((claim) => (
            <div
              key={claim.key}
              className={cn({ hidden: claim.key !== selectedClaimKey })}
            >
              <PatientClaimForm
                ref={(node) => {
                  if (node) {
                    formRefsMap.current.set(claim.key, node)
                  } else {
                    formRefsMap.current.delete(claim.key)
                  }
                }}
                claim={claim}
                onNameChange={(first, last) => handleNameChange(claim.key, first, last)}
                onModifiedChange={(isModified) => handleModifiedChange(claim.key, isModified)}
                onValidChange={(isValid) => handleValidChange(claim.key, isValid)}
                onCanUndoChange={(canUndo) => handleCanUndoChange(claim.key, canUndo)}
                onCanRedoChange={(canRedo) => handleCanRedoChange(claim.key, canRedo)}
                onLocalFlagChange={(localFlag) => handleLocalFlagChange(claim.key, localFlag)}
              />
            </div>
          ))}

          {sentinelElementBottom}
        </ScrollArea>
      </div>
      {deleteDialogKey && (
        <DeleteClaimDialog
          onClose={() => setDeleteDialogKey(null)}
          onConfirm={handleDeleteConfirm}
          isPending={deleteClaim.isPending}
        />
      )}
    </div>
  )
}
